import { PlanType } from '@labrav/react-components'
import type { ProductDimension } from '../../productsListing/types'
import { getUpdatedDimensionsForEmptyPOAndResaleContract } from '../privateOfferCreation/utils/dimensionHelper'
import type { OffersWithErrors } from '../privateOfferCreation/types'
import { OfferStatus, type Dimension, type PrivateOffer } from '../types'

const dimensionIdentityKeys = (dimension: {
  dimensionId?: string
  name?: string
}) => {
  const keys: string[] = []
  if (dimension.dimensionId) {
    keys.push(`id:${dimension.dimensionId}`)
  }
  if (dimension.name) {
    keys.push(`name:${dimension.name}`)
  }
  return keys
}

const addMissingUsageDimensions = ({
  offerDimensions,
  productDimensions,
  awsProductId,
}: {
  offerDimensions: Dimension[]
  productDimensions?: ProductDimension[]
  awsProductId?: string
}): Dimension[] => {
  const clonedDimensions = offerDimensions.map(dimension => ({
    ...dimension,
    ...(awsProductId ? { awsProductId } : {}),
  }))
  const seen = new Set<string>()
  clonedDimensions.forEach(dimension => {
    dimensionIdentityKeys(dimension).forEach(key => seen.add(key))
  })

  const addedUsageDimensions: Dimension[] = []
  const productUsage = getUpdatedDimensionsForEmptyPOAndResaleContract(
    productDimensions || []
  ) as Dimension[]

  productUsage.forEach(productDimension => {
    const keys = dimensionIdentityKeys(productDimension)
    if (!keys.length || keys.some(key => seen.has(key))) {
      return
    }
    const name = productDimension.name || ''
    const label = productDimension.label || ''
    const labelDescription = productDimension.labelDescription || label || ''
    if (!name || !labelDescription) {
      return
    }
    keys.forEach(key => seen.add(key))
    addedUsageDimensions.push({
      ...productDimension,
      name,
      label,
      labelDescription,
      currency: productDimension.currency || 'USD',
      isAdditionalUsageAllowed: true,
      type: 'additional_usage',
      ...(awsProductId ? { awsProductId } : {}),
    })
  })

  return [...clonedDimensions, ...addedUsageDimensions]
}

const isListingUsageDimension = (dimension: ProductDimension) =>
  dimension.isAdditionalUsageAllowed === true

const isUsageOfferDimension = (dimension: Dimension) =>
  dimension.type === 'additional_usage' ||
  dimension.isAdditionalUsageAllowed === true

const isDimensionPresentInOffer = (
  offerDimensions: Dimension[],
  dimension: ProductDimension
) => {
  const keys = dimensionIdentityKeys({
    dimensionId: dimension.id,
    name: dimension.name,
  })
  if (!keys.length) {
    return true
  }
  return offerDimensions.some(offerDimension => {
    const offerKeys = dimensionIdentityKeys(offerDimension)
    return keys.some(key => offerKeys.includes(key))
  })
}

const isDimensionPresentInListing = (
  offerDimension: Dimension,
  productDimensions: ProductDimension[]
) => {
  const keys = dimensionIdentityKeys(offerDimension)
  if (!keys.length) {
    return true
  }
  const isUsage = isUsageOfferDimension(offerDimension)
  return productDimensions
    .filter(dimension => isListingUsageDimension(dimension) === isUsage)
    .some(dimension => {
      const listingKeys = dimensionIdentityKeys({
        dimensionId: dimension.id,
        name: dimension.name,
      })
      return keys.some(key => listingKeys.includes(key))
    })
}

export type CloneDimensionUpdate = 'both' | 'product' | 'usage'

/** When offer dimensions are missing from the listing, or listing usage was added. */
export const getCloneDimensionUpdate = (
  offerDimensions: Dimension[] | undefined,
  productDimensions: ProductDimension[] | undefined
): CloneDimensionUpdate | undefined => {
  const offer = offerDimensions || []
  const product = productDimensions || []
  const listingAddedUsage = product
    .filter(isListingUsageDimension)
    .some(dimension => !isDimensionPresentInOffer(offer, dimension))
  const offerRemovedProduct = offer
    .filter(dimension => !isUsageOfferDimension(dimension))
    .some(dimension => !isDimensionPresentInListing(dimension, product))
  const offerRemovedUsage = offer
    .filter(isUsageOfferDimension)
    .some(dimension => !isDimensionPresentInListing(dimension, product))
  const productDimensionsUpdated = offerRemovedProduct
  const usageDimensionsUpdated = listingAddedUsage || offerRemovedUsage

  if (productDimensionsUpdated && usageDimensionsUpdated) {
    return 'both'
  }
  if (productDimensionsUpdated) {
    return 'product'
  }
  if (usageDimensionsUpdated) {
    return 'usage'
  }
  return undefined
}

export const getOfferForCloneOffer = ({
  privateOffer,
  productType,
  productDimensions,
}: {
  privateOffer: PrivateOffer
  productType?: PlanType
  productDimensions?: ProductDimension[]
}) => {
  const {
    privateOfferName,
    flexiblePaymentTerms,
    awsProductId,
    companyName,
    crmOrderObjectId,
    crmOpportunityId,
    awsServiceStartDate,
    awsAccountId,
    serviceLength,
    entitlementOption,
    eulaType,
    eulaFile,
    renewal,
    renewalChannel,
    autoRenew,
    zeroDollarPricing,
    offerRecipients,
    installments,
    firstInstallmentDate,
    flexiblePaymentType,
    tcv,
    subscriptionEndDate,
    emailCustomMessage,
    metaData,
    dimensions,
    variableStartDate,
    offerStakeholders,
    customOfferStakeholders,
    agreementStartDate,
    agreementEndDate,
  } = privateOffer

  const offerRecipientsData = (offerRecipients || [])?.map(
    offerRecipientItem => ({
      firstName: offerRecipientItem?.firstName,
      lastName: offerRecipientItem?.lastName,
      email: offerRecipientItem?.email,
      title: offerRecipientItem?.title,
      crmObjectId: offerRecipientItem?.crmObjectId,
    })
  )
  const metaDataDetail = (metaData || [])?.map(metaDataItem => ({
    key: metaDataItem?.key,
    value: metaDataItem?.value,
    crmObjectId: metaDataItem?.crmObjectId,
  }))
  const installmentsData = (installments || [])?.map(installmentItem => ({
    amount: installmentItem?.amount,
    paymentDate: '',
    currency: installmentItem?.currency,
  }))

  return {
    privateOfferName: `Clone ${privateOfferName}`,
    flexiblePaymentTerms,
    companyName,
    crmOrderObjectId: crmOrderObjectId || '',
    crmOpportunityId,
    awsServiceStartDate,
    awsAccountId,
    serviceLength,
    eulaType,
    eulaFile,
    firstInstallmentDate,
    tcv,
    renewal,
    renewalChannel,
    autoRenew,
    offerExpirationDate: '',
    agreementStartDate: agreementStartDate || '',
    zeroDollarPricing,
    offerRecipients: offerRecipientsData,
    installments: installmentsData,
    flexiblePaymentType,
    emailCustomMessage,
    metaData: metaDataDetail,
    dimensions:
      productType === PlanType.SAAS_CONTRACT
        ? addMissingUsageDimensions({
            offerDimensions: dimensions || [],
            productDimensions,
            awsProductId,
          })
        : (dimensions || [])?.map(dimensionItem => ({
            ...dimensionItem,
            awsProductId,
          })),
    variableStartDate,
    offerStakeholders,
    customOfferStakeholders,
    awsProductId,
    draft: true,
    offerStatus: OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS,
    ...(productType === PlanType.SAAS_CONTRACT
      ? {
          entitlementOption,
          subscriptionEndDate: subscriptionEndDate || agreementEndDate || '',
          agreementEndDate: agreementEndDate ?? subscriptionEndDate ?? null,
        }
      : {}),
    ...(productType === PlanType.SAAS_SUBSCRIPTION
      ? {
          subscriptionEndDate,
        }
      : {}),
  } as OffersWithErrors
}
